import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { Location } from '../../types/location';
import { toast } from 'react-toastify';
import { QRCodeSVG } from 'qrcode.react';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import Modal from '../Modal';
import { debounce } from 'lodash';

interface PriceSchedule {
  startTime: string;
  endTime: string;
  price: number;
}

interface Connector {
  connectorId: number;
  status: 'Available' | 'Preparing' | 'Charging' | 'SuspendedEVSE' | 'SuspendedEV' | 
          'Finishing' | 'Reserved' | 'Unavailable' | 'Faulted';
  errorCode: string;
  lastUpdated: Date;
  qrCode?: string;
}

interface ChargePoint {
  _id: string;
  chargePointId: string;
  password?: string;
  name: string;
  locationId: {
    _id: string;
    name: string;
    address: string;
  } | string;
  vendor: string;
  model: string;
  mode: 'public' | 'private';
  vehicleType: 'motorbike' | 'car' | 'both';
  operatorId: {
    _id: string;
    name: string;
    currency: 'USD' | 'VND';
  } | string;
  type: 'AC' | 'DC';
  status: 'available' | 'in-use' | 'maintenance' | 'offline';
  power: number;
  connector: 'Type1' | 'Type2' | 'CHAdeMO' | 'CCS' | 'CCS2';
  prices: PriceSchedule[];
  connectors: Connector[];
}

interface Vendor {
  _id: string;
  name: string;
  description?: string;
  website?: string;
  status?: 'active' | 'inactive';
}

interface NewChargePoint {
  name: string;
  chargePointId: string;
  locationId: string;
  vendor: string;
  model: string;
  mode: 'public' | 'private';
  vehicleType: 'motorbike' | 'car' | 'both';
  type: 'AC' | 'DC';
  power: number;
  connector: 'Type1' | 'Type2' | 'CHAdeMO' | 'CCS' | 'CCS2';
  prices: PriceSchedule[];
  connectors: {
    connectorId: number;
    status: string;
  }[];
}

const modalStyles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  content: {
    position: 'relative' as const,
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    maxWidth: '90%',
    maxHeight: '90%'
  },
  closeButton: {
    position: 'absolute' as const,
    right: '10px',
    top: '10px',
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer'
  }
};

const formatPrice = (price: number, operatorId: any) => {
  if (!operatorId || typeof operatorId !== 'object') {
    return `${price}`;
  }
  
  const currency = operatorId.currency || 'USD';
  if (currency === 'USD') {
    return `$${price.toFixed(2)}`;
  } else {
    return `${price.toLocaleString('vi-VN')}đ`;
  }
};

const Icons = {
  qrcode: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 3h6v6H3zm2 2v2h2V5zm-2 8h6v6H3zm2 2v2h2v-2zm8-12h6v6h-6zm2 2v2h2V5zm3 13h2v2h-2zm-3-3h2v2h-2zm2 0v-2h2v2zm-2-4h4v2h-4zm2-8h2v4h-2z"/>
    </svg>
  ),
  unlock: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm6-9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h9V6a3 3 0 0 0-6 0h-2a5 5 0 1 1 10 0v2h1z"/>
    </svg>
  ),
  close: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
    </svg>
  ),
  lock: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm6-9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h1V6a5 5 0 1 1 10 0v2h1zm-6-5a3 3 0 0 0-3 3v2h6V6a3 3 0 0 0-3-3z"/>
    </svg>
  ),
  eye: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
    </svg>
  ),
  eyeOff: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
    </svg>
  ),
  threeDots: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
    </svg>
  ),
  copy: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
    </svg>
  )
};

const isConnectorLocked = (status: string) => {
  return status === 'Reserved' || status === 'Charging' || 
         status === 'SuspendedEVSE' || status === 'SuspendedEV' || 
         status === 'Preparing' || status === 'Finishing';
};

const printQRCode = () => {
  const printContent = document.getElementById('qr-code-content');
  if (!printContent) return;

  const printWindow = window.open('', '', 'height=600,width=800');
  if (!printWindow) return;

  printWindow.document.write('<html><head><title>Print QR Code</title>');
  printWindow.document.write(`
    <style>
      body { 
        font-family: Arial, sans-serif;
        text-align: center;
        padding: 20px;
      }
      .qr-container {
        margin: 20px auto;
      }
      p { 
        margin: 10px 0;
        font-size: 14px;
      }
      @media print {
        .no-print {
          display: none;
        }
      }
    </style>
  `);
  
  // Clone the content and remove the buttons before printing
  const contentClone = printContent.cloneNode(true) as HTMLElement;
  const buttons = contentClone.querySelector('.mt-4.flex.gap-4');
  if (buttons) {
    buttons.remove();
  }
  
  printWindow.document.write('</head><body>');
  printWindow.document.write(contentClone.innerHTML);
  printWindow.document.write('</body></html>');
  printWindow.document.close();

  printWindow.onload = function() {
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };
};

const ChargePointList: React.FC = () => {
  const { t } = useTranslation();
  const [chargePoints, setChargePoints] = useState<ChargePoint[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingChargePoint, setEditingChargePoint] = useState<ChargePoint | null>(null);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState<{
    chargePointId: string;
    connectorId: number;
    qrCode: string;
  } | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedChargePointId, setSelectedChargePointId] = useState<string>('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [selectedUrlChargePoint, setSelectedUrlChargePoint] = useState<ChargePoint | null>(null);
  const [currentUrlPassword, setCurrentUrlPassword] = useState<string>('');
  
  // Add state for location search modal
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  const [searchedLocations, setSearchedLocations] = useState<Location[]>([]);
  const [isSearchingLocations, setIsSearchingLocations] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);

  // Add pagination and filter states
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    operatorId: '',
    type: '',
    vehicleType: ''
  });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Table container ref for scroll detection
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const [newChargePoint, setNewChargePoint] = useState<NewChargePoint>({
    name: '',
    chargePointId: '',
    locationId: '',
    vendor: '',
    model: '',
    mode: 'public',
    vehicleType: 'both',
    type: 'AC',
    power: 0,
    connector: 'Type2',
    prices: [
      { startTime: '00:00', endTime: '24:00', price: 0 }
    ],
    connectors: [
      { connectorId: 1, status: 'Available' }
    ]
  });

  // Add state for operators
  const [operators, setOperators] = useState<{_id: string, name: string, company: string}[]>([]);

  useEffect(() => {
    fetchChargePoints(1);
    fetchLocations();
    fetchVendors();
    fetchOperators();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeDropdown && !(event.target as Element).closest('.dropdown-content')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeDropdown]);

  // Add debounced search
  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      const newFilters = { ...filters, search: searchTerm };
      setFilters(newFilters);
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchChargePoints(1, 20, newFilters);
    }, 300),
    [filters]
  );

  // Add scroll handler for infinite scrolling
  const handleTableScroll = useCallback(() => {
    if (!tableContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;
    const scrollPosition = scrollTop + clientHeight;
    const scrollThreshold = scrollHeight - 200; // Load more when 200px from bottom
    
    console.log(`Scroll position: ${scrollPosition}, threshold: ${scrollThreshold}`);
    
    if (scrollPosition >= scrollThreshold && !isLoadingMore) {
      console.log('Loading more charge points...');
      loadMoreItems();
    }
  }, [isLoadingMore, pagination]);

  // Load more function for infinite scrolling
  const loadMoreItems = useCallback(() => {
    if (pagination.page < pagination.totalPages) {
      console.log(`Loading page ${pagination.page + 1} of ${pagination.totalPages}`);
      fetchChargePoints(pagination.page + 1, pagination.limit, filters);
    } else {
      console.log('No more pages to load');
    }
  }, [pagination, filters]);

  const fetchChargePoints = async (page: number, limit: number = 20, currentFilters = filters) => {
    try {
      console.log(`Fetching charge points: page=${page}, limit=${limit}, filters=`, currentFilters);
      setIsLoadingMore(page > 1);
      setLoading(page === 1);
      
      const response = await api.get('/charge-points', {
        params: {
          page,
          limit,
          search: currentFilters.search,
          status: currentFilters.status,
          operatorId: currentFilters.operatorId,
          type: currentFilters.type,
          vehicleType: currentFilters.vehicleType
        }
      });
      
      console.log(`Received ${response.data.chargePoints.length} charge points for page ${page}`, response.data);
      
      // Update charge points state
      if (page === 1) {
        setChargePoints(response.data.chargePoints || []);
      } else if (response.data.chargePoints && response.data.chargePoints.length > 0) {
        setChargePoints(prev => [...prev, ...response.data.chargePoints]);
      }
      
      // Update pagination info
      setPagination({
        page: response.data.page,
        limit: response.data.limit,
        total: response.data.total,
        totalPages: response.data.totalPages
      });
      
      console.log(`Updated charge points: now have ${page === 1 ? response.data.chargePoints.length : chargePoints.length + response.data.chargePoints.length} items`);
      
      setLoading(false);
      setIsLoadingMore(false);
    } catch (err) {
      console.error('Error fetching charge points:', err);
      setError('Failed to fetch charge points');
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await api.get('/locations');
      // Handle both array format and object with locations property
      const locationData = Array.isArray(response.data) 
        ? response.data 
        : (response.data.locations || []);
      setLocations(locationData);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError('Failed to fetch locations');
    }
  };

  const fetchVendors = async () => {
    try {
      console.log('Fetching vendors...');
      const response = await api.get('/vendors');
      console.log('Vendors response:', response.data);
      setVendors(response.data);
    } catch (err) {
      console.error('Failed to fetch vendors:', err);
      toast.error(t('vendors.messages.fetchError'));
    }
  };

  // Add function to fetch operators
  const fetchOperators = async () => {
    try {
      console.log('Fetching operators...');
      const response = await api.get('/operators');
      console.log('Operators response:', response.data);
      setOperators(response.data);
    } catch (err) {
      console.error('Failed to fetch operators:', err);
      toast.error(t('operators.messages.fetchError'));
    }
  };

  const handleCopyUrl = async (chargePoint: ChargePoint) => {
    try {
      const response = await api.get(`/charge-points/${chargePoint._id}`);
      setCurrentUrlPassword(response.data.password || '');
      setSelectedUrlChargePoint(chargePoint);
      setShowUrlModal(true);
      setActiveDropdown(null);
    } catch (error) {
      toast.error(t('chargePoints.messages.fetchError'));
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t('chargePoints.messages.copied'));
  };

  const resetForm = () => {
    setNewChargePoint({
      name: '',
      chargePointId: '',
      locationId: '',
      vendor: '',
      model: '',
      mode: 'public',
      vehicleType: 'both',
      type: 'AC',
      power: 0,
      connector: 'Type2',
      prices: [
        { startTime: '00:00', endTime: '24:00', price: 0 }
      ],
      connectors: [
        { connectorId: 1, status: 'Available' }
      ]
    });
    setEditingChargePoint(null);
    setShowForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      console.log('Submitting data:', newChargePoint);

      const submitData = {
        ...newChargePoint,
        name: newChargePoint.name,
        chargePointId: newChargePoint.chargePointId,
        locationId: newChargePoint.locationId || undefined,
        vendor: newChargePoint.vendor || null,
        model: newChargePoint.model || null,
        mode: newChargePoint.mode,
        vehicleType: newChargePoint.vehicleType,
        type: newChargePoint.type,
        power: newChargePoint.power,
        connector: newChargePoint.connector,
        prices: newChargePoint.prices || [],
        connectors: newChargePoint.connectors.map(c => ({
          connectorId: c.connectorId,
          status: c.status || 'Available'
        }))
      };

      if (editingChargePoint) {
        const response = await api.put(`/charge-points/${editingChargePoint._id}`, submitData);
        console.log('Update response:', response.data);
      } else {
        await api.post('/charge-points', submitData);
      }
      fetchChargePoints(1, 20, filters);
      setShowForm(false);
      setEditingChargePoint(null);
      resetForm();
      toast.success(editingChargePoint ? t('chargePoints.messages.updateSuccess') : t('chargePoints.messages.createSuccess'));
    } catch (err: any) {
      console.error('Submit error:', err.response?.data || err);
      toast.error(err.response?.data?.message || t('chargePoints.messages.saveError'));
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('chargePoints.messages.deleteConfirm'))) {
      try {
        await api.delete(`/charge-points/${id}`);
        toast.success(t('chargePoints.messages.deleteSuccess'));
        fetchChargePoints(1, 20, filters);
      } catch (error) {
        toast.error(t('chargePoints.messages.deleteError'));
      }
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'available':
        return 'bg-green-100 text-green-800';
      case 'in-use':
        return 'bg-blue-100 text-blue-800';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800';
      case 'offline':
        return 'bg-red-100 text-red-800';
      case 'charging':
        return 'bg-blue-100 text-blue-800';
      case 'faulted':
        return 'bg-red-100 text-red-800';
      case 'reserved':
        return 'bg-yellow-100 text-yellow-800';
      case 'preparing':
      case 'finishing':
      case 'suspendedevse':
      case 'suspendedev':
        return 'bg-yellow-100 text-yellow-800';
      case 'unavailable':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleEdit = (chargePoint: ChargePoint) => {
    setEditingChargePoint(chargePoint);
    setNewChargePoint({
      name: chargePoint.name,
      chargePointId: chargePoint.chargePointId,
      locationId: typeof chargePoint.locationId === 'object' && chargePoint.locationId ? chargePoint.locationId._id : (chargePoint.locationId || ''),
      vendor: chargePoint.vendor || '',
      model: chargePoint.model || '',
      mode: chargePoint.mode,
      vehicleType: chargePoint.vehicleType,
      type: chargePoint.type,
      power: chargePoint.power,
      connector: chargePoint.connector,
      prices: chargePoint.prices.length > 0 
        ? chargePoint.prices 
        : [{ startTime: '00:00', endTime: '24:00', price: 0 }],
      connectors: chargePoint.connectors.map(connector => ({
        connectorId: connector.connectorId,
        status: connector.status
      }))
    });
    setShowForm(true);
  };

  const getCurrentPrice = (prices: PriceSchedule[] | undefined): number => {
    if (!prices || prices.length === 0) {
      return 0;
    }

    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    
    const currentSchedule = prices.find(schedule => {
      if (!schedule || !schedule.startTime || !schedule.endTime) {
        return false;
      }

      const current = currentTime;
      const start = schedule.startTime;
      const end = schedule.endTime;
      
      if (start <= end) {
        return current >= start && current < end;
      } else {
        return current >= start || current < end;
      }
    });

    return currentSchedule?.price || prices[0]?.price || 0;
  };

  const handleRestart = async (chargePoint: ChargePoint) => {
    if (window.confirm(t('chargePoints.confirmations.restart'))) {
      try {
        await api.post(`/charge-points/${chargePoint._id}/restart`);
        toast.success('Restart command sent successfully');
      } catch (error) {
        toast.error('Failed to send restart command');
      }
    }
  };

  const handleUnlock = async (chargePoint: ChargePoint, connectorId: number) => {
    const connector = chargePoint.connectors.find(c => c.connectorId === connectorId);
    if (!connector) return;

    const isLocked = isConnectorLocked(connector.status);
    const action = isLocked ? 'unlock' : 'lock';
    
    if (window.confirm(t(`chargePoints.confirmations.${action}`, { number: connectorId }))) {
      try {
        const response = await api.post(`/charge-points/${chargePoint._id}/${action}/${connectorId}`);
        await fetchChargePoints(1, 20, filters);
        toast.success(`${action} command sent successfully`);
      } catch (error) {
        toast.error(`Failed to send ${action} command`);
      }
    }
  };

  const handleQRCode = (chargePoint: ChargePoint, connector: Connector) => {
    const locationId = typeof chargePoint.locationId === 'object' 
      ? chargePoint.locationId._id 
      : chargePoint.locationId;

    setSelectedConnector({
      chargePointId: chargePoint.chargePointId,
      connectorId: connector.connectorId,
      qrCode: connector.qrCode || `${chargePoint.chargePointId}:${connector.connectorId}:${locationId}`
    });
    setQrModalVisible(true);
  };

  const downloadQRCode = () => {
    const svgElement = document.querySelector('#qr-code svg');
    if (!svgElement || !selectedConnector) return;

    // Create a larger canvas for better quality
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set higher resolution - 512px with 20px margin on each side
    const margin = 40;
    const size = 512;
    canvas.width = size + (margin * 2);
    canvas.height = size + (margin * 2);

    // Fill with white background first
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Convert SVG to a data URL
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const DOMURL = window.URL || window.webkitURL || window;
    const url = DOMURL.createObjectURL(svgBlob);

    // Create an image from the SVG
    const img = new Image();
    img.onload = () => {
      // Draw SVG on canvas with margin
      ctx.drawImage(img, margin, margin, size, size);

      // Add a title/label at the bottom
      ctx.font = 'bold 20px Arial';
      ctx.fillStyle = 'black';
      ctx.textAlign = 'center';
      //ctx.fillText(`${selectedConnector.chargePointId} - Connector #${selectedConnector.connectorId}`, canvas.width / 2, canvas.height - 10);

      // Generate PNG data URL
      const pngUrl = canvas.toDataURL('image/png');
      
      // Create download link
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `qrcode_${selectedConnector.chargePointId}_${selectedConnector.connectorId}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      // Clean up
      DOMURL.revokeObjectURL(url);
    };
    img.src = url;
  };

  const handlePasswordUpdate = async (passwordToSet: string = newPassword) => {
    try {
      await api.patch(`/charge-points/${selectedChargePointId}/password`, { password: passwordToSet });
      setShowPasswordModal(false);
      setNewPassword('');
      setCurrentPassword('');
      setShowPassword(false);
      await fetchChargePoints(1, 20, filters);
      toast.success(passwordToSet === '' ? 
        t('chargePoints.messages.passwordRemoveSuccess') : 
        t('chargePoints.messages.passwordUpdateSuccess')
      );
    } catch (error) {
      toast.error(t('chargePoints.messages.passwordUpdateError'));
    }
  };

  const handleEditPassword = async (chargePointId: string) => {
    try {
      const response = await api.get(`/charge-points/${chargePointId}`);
      setCurrentPassword(response.data.password || '');
      setNewPassword(response.data.password || '');
      setSelectedChargePointId(chargePointId);
      setShowPasswordModal(true);
    } catch (error) {
      toast.error(t('chargePoints.messages.fetchError'));
    }
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t('chargePoints.messages.copied'));
  };

  const handleShowUrlModal = async (chargePoint: ChargePoint) => {
    try {
      const response = await api.get(`/charge-points/${chargePoint._id}`);
      setSelectedUrlChargePoint({
        ...chargePoint,
        password: response.data.password || ''
      });
      setShowUrlModal(true);
      setActiveDropdown(null);
    } catch (error) {
      toast.error(t('chargePoints.messages.fetchError'));
    }
  };

  // Add filters display
  const renderFilters = () => (
    <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
      <div>
        <input
          type="text"
          placeholder={t('chargePoints.search')}
          className="w-full px-3 py-2 border rounded"
          onChange={(e) => debouncedSearch(e.target.value)}
        />
      </div>
      <div>
        <select
          className="w-full px-3 py-2 border rounded"
          value={filters.status}
          onChange={(e) => {
            const newFilters = { ...filters, status: e.target.value };
            setFilters(newFilters);
            setPagination(prev => ({ ...prev, page: 1 }));
            fetchChargePoints(1, pagination.limit, newFilters);
          }}
        >
          <option value="">{t('chargePoints.allStatuses')}</option>
          <option value="available">{t('chargePoints.chargePointStatus.available')}</option>
          <option value="in-use">{t('chargePoints.chargePointStatus.in-use')}</option>
          <option value="maintenance">{t('chargePoints.chargePointStatus.maintenance')}</option>
          <option value="offline">{t('chargePoints.chargePointStatus.offline')}</option>
        </select>
      </div>
      <div>
        <select
          className="w-full px-3 py-2 border rounded"
          value={filters.type}
          onChange={(e) => {
            const newFilters = { ...filters, type: e.target.value };
            setFilters(newFilters);
            setPagination(prev => ({ ...prev, page: 1 }));
            fetchChargePoints(1, pagination.limit, newFilters);
          }}
        >
          <option value="">{t('chargePoints.allTypes')}</option>
          <option value="AC">AC</option>
          <option value="DC">DC</option>
        </select>
      </div>
      <div>
        <select
          className="w-full px-3 py-2 border rounded"
          value={filters.vehicleType}
          onChange={(e) => {
            const newFilters = { ...filters, vehicleType: e.target.value };
            setFilters(newFilters);
            setPagination(prev => ({ ...prev, page: 1 }));
            fetchChargePoints(1, pagination.limit, newFilters);
          }}
        >
          <option value="">{t('chargePoints.allVehicleTypes')}</option>
          <option value="car">{t('chargePoints.vehicleType.car')}</option>
          <option value="motorbike">{t('chargePoints.vehicleType.motorbike')}</option>
          <option value="both">{t('chargePoints.vehicleType.both')}</option>
        </select>
      </div>
      <div>
        <select
          className="w-full px-3 py-2 border rounded"
          value={filters.operatorId}
          onChange={(e) => {
            const newFilters = { ...filters, operatorId: e.target.value };
            setFilters(newFilters);
            setPagination(prev => ({ ...prev, page: 1 }));
            fetchChargePoints(1, pagination.limit, newFilters);
          }}
        >
          <option value="">{t('chargePoints.allOperators')}</option>
          {operators.map(operator => (
            <option key={operator._id} value={operator._id}>
              {operator.name} ({operator.company})
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  // Add count display
  const renderCountSummary = () => {
    // Find combined filter description
    let filterDescription = '';
    const activeFilters = [];
    
    if (filters.status) {
      activeFilters.push(t(`chargePoints.chargePointStatus.${filters.status}`, filters.status));
    }
    
    if (filters.type) {
      activeFilters.push(filters.type);
    }
    
    if (filters.vehicleType) {
      activeFilters.push(t(`chargePoints.vehicleType.${filters.vehicleType}`, filters.vehicleType));
    }
    
    if (filters.operatorId) {
      // Safely find the operator name
      let operatorName = filters.operatorId;
      
      const operator = operators.find(op => op._id === filters.operatorId);
      if (operator) {
        operatorName = `${operator.name} (${operator.company})`;
      }
      
      activeFilters.push(operatorName);
    }
    
    if (activeFilters.length > 0) {
      filterDescription = activeFilters.join(', ');
    }
    
    return (
      <div className="mb-4 text-gray-600 font-medium">
        {filters.search ? (
          <p>
            {t(
              'chargePoints.searchResults', 
              `Found ${pagination.total} charge points matching "${filters.search}"`,
              { count: pagination.total, term: filters.search }
            )}
          </p>
        ) : filterDescription ? (
          <p>
            {t(
              'chargePoints.filterResults', 
              `Showing ${chargePoints.length} of ${pagination.total} charge points (${filterDescription})`,
              { 
                showing: chargePoints.length,
                total: pagination.total, 
                filters: filterDescription
              }
            )}
          </p>
        ) : (
          <p>
            {t(
              'chargePoints.totalCount', 
              `Showing ${chargePoints.length} of ${pagination.total} total charge points`,
              { showing: chargePoints.length, total: pagination.total }
            )}
          </p>
        )}
      </div>
    );
  };

  // Add a function to search locations
  const searchAllLocations = async (term: string) => {
    if (term.length < 2) {
      setSearchedLocations([]);
      return;
    }
    
    try {
      setIsSearchingLocations(true);
      console.log('Searching locations with term:', term);
      
      // This is the same API call pattern as in LocationList.tsx
      const response = await api.get('/locations', {
        params: {
          page: 1,
          limit: 100,
          search: term
        }
      });
      
      console.log('Location search response:', response.data);
      
      // The LocationList.tsx implementation accesses .locations property
      if (response.data && response.data.locations) {
        setSearchedLocations(response.data.locations);
        console.log(`Found ${response.data.locations.length} matching locations out of ${response.data.total}`);
      } else {
        console.log('No locations property in response, using entire response array');
        setSearchedLocations(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Error searching locations:', error);
      setSearchedLocations([]);
    } finally {
      setIsSearchingLocations(false);
    }
  };
  
  // Add a debounced version of the search function
  const debouncedLocationSearch = useCallback(
    debounce((term: string) => searchAllLocations(term), 300),
    []
  );

  const selectLocation = (location: Location) => {
    console.log('Location selected:', location);
    setSelectedLocation(location);
    setNewChargePoint({ ...newChargePoint, locationId: location._id });
    setShowLocationModal(false);
  };

  if (loading && chargePoints.length === 0) {
    return <div>{t('common.loading')}</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">{t('chargePoints.title')}</h1>
        <div className="mb-4">
          <button
            type="button"
            onClick={() => {
              if (showForm) {
                resetForm();
              }
              setShowForm(!showForm);
            }}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            {showForm ? t('common.cancel') : t('chargePoints.addChargePoint')}
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('chargePoints.chargePointId')}
              </label>
              <input
                type="text"
                value={newChargePoint.chargePointId}
                onChange={(e) => setNewChargePoint({ ...newChargePoint, chargePointId: e.target.value })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
                placeholder={t('chargePoints.messages.enterChargePointId')}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('chargePoints.name')}
              </label>
              <input
                type="text"
                value={newChargePoint.name}
                onChange={(e) => setNewChargePoint({ ...newChargePoint, name: e.target.value })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('chargePoints.location')}
              </label>
              <div className="flex flex-col space-y-2">
                {editingChargePoint && (
                  <div className="text-sm text-gray-600">
                    {t('chargePoints.currentLocation')}: {
                      typeof editingChargePoint.locationId === 'object' && editingChargePoint.locationId 
                        ? editingChargePoint.locationId.name 
                        : t('chargePoints.messages.noLocation')
                    }
                  </div>
                )}
                
                {selectedLocation && (
                  <div className="p-2 bg-blue-50 border border-blue-200 rounded">
                    <div className="font-medium">{selectedLocation.name}</div>
                    {selectedLocation.address && typeof selectedLocation.address === 'object' && (
                      <div className="text-sm text-gray-600">
                        {selectedLocation.address.street}, {selectedLocation.address.city}, {selectedLocation.address.country}
                      </div>
                    )}
                  </div>
                )}
                
                <button
                  type="button"
                  onClick={() => setShowLocationModal(true)}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {selectedLocation 
                    ? t('chargePoints.changeLocation') 
                    : t('chargePoints.searchAndSelectLocation')}
                </button>
                
                {editingChargePoint && (
                  <p className="text-xs text-gray-500">{t('chargePoints.messages.leaveUnchanged')}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('chargePoints.type')}
              </label>
              <select
                value={newChargePoint.type}
                onChange={(e) => setNewChargePoint({ ...newChargePoint, type: e.target.value as 'AC' | 'DC' })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              >
                <option value="AC">{t('chargePoints.chargeTypes.AC')}</option>
                <option value="DC">{t('chargePoints.chargeTypes.DC')}</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('chargePoints.power')}
              </label>
              <input
                type="number"
                value={newChargePoint.power}
                onChange={(e) => setNewChargePoint({ ...newChargePoint, power: parseFloat(e.target.value) })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
                min="0"
                step="0.1"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('chargePoints.connector')}
              </label>
              <select
                value={newChargePoint.connector}
                onChange={(e) => setNewChargePoint({ ...newChargePoint, connector: e.target.value as 'Type1' | 'Type2' | 'CHAdeMO' | 'CCS' | 'CCS2' })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              >
                <option value="Type1">{t('chargePoints.connectorTypes.Type1')}</option>
                <option value="Type2">{t('chargePoints.connectorTypes.Type2')}</option>
                <option value="CHAdeMO">{t('chargePoints.connectorTypes.CHAdeMO')}</option>
                <option value="CCS">{t('chargePoints.connectorTypes.CCS')}</option>
                <option value="CCS2">{t('chargePoints.connectorTypes.CCS2')}</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('chargePoints.vendor')}
              </label>
              <select
                value={newChargePoint.vendor}
                onChange={(e) => {
                  console.log('Selected vendor:', e.target.value);
                  setNewChargePoint({ ...newChargePoint, vendor: e.target.value });
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              >
                <option value="">{t('chargePoints.selectVendor')}</option>
                {vendors.map((vendor) => (
                  <option key={vendor._id} value={vendor.name}>
                    {vendor.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('chargePoints.model')}
              </label>
              <input
                type="text"
                value={newChargePoint.model}
                onChange={(e) => setNewChargePoint({ ...newChargePoint, model: e.target.value })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('chargePoints.mode.title')}
              </label>
              <select
                value={newChargePoint.mode}
                onChange={(e) => setNewChargePoint({ ...newChargePoint, mode: e.target.value as 'public' | 'private' })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              >
                <option value="public">{t('chargePoints.mode.public')}</option>
                <option value="private">{t('chargePoints.mode.private')}</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('chargePoints.vehicleType.title')}
              </label>
              <select
                value={newChargePoint.vehicleType}
                onChange={(e) => setNewChargePoint({ ...newChargePoint, vehicleType: e.target.value as 'motorbike' | 'car' | 'both' })}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                required
              >
                <option value="motorbike">{t('chargePoints.vehicleType.motorbike')}</option>
                <option value="car">{t('chargePoints.vehicleType.car')}</option>
                <option value="both">{t('chargePoints.vehicleType.both')}</option>
              </select>
            </div>

            <div className="mt-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('chargePoints.priceSchedules')}
              </label>
              {newChargePoint.prices.map((schedule, index) => (
                <div key={index} className="flex gap-4 mb-2">
                  <div>
                    <label className="block text-xs">{t('chargePoints.startTime')}</label>
                    <input
                      type="time"
                      value={schedule.startTime}
                      onChange={(e) => {
                        const newPrices = [...newChargePoint.prices];
                        newPrices[index].startTime = e.target.value;
                        setNewChargePoint({ ...newChargePoint, prices: newPrices });
                      }}
                      className="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      required
                      disabled={newChargePoint.prices.length === 1}
                    />
                  </div>
                  <div>
                    <label className="block text-xs">{t('chargePoints.endTime')}</label>
                    <input
                      type="time"
                      value={schedule.endTime}
                      onChange={(e) => {
                        const newPrices = [...newChargePoint.prices];
                        newPrices[index].endTime = e.target.value;
                        setNewChargePoint({ ...newChargePoint, prices: newPrices });
                      }}
                      className="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      required
                      disabled={newChargePoint.prices.length === 1}
                    />
                  </div>
                  <div>
                    <label className="block text-xs">{t('chargePoints.price')}</label>
                    <input
                      type="number"
                      value={schedule.price}
                      onChange={(e) => {
                        const newPrices = [...newChargePoint.prices];
                        newPrices[index].price = parseFloat(e.target.value);
                        setNewChargePoint({ ...newChargePoint, prices: newPrices });
                      }}
                      className="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      required
                      min="0"
                      step="0.01"
                    />
                  </div>
                  {newChargePoint.prices.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newPrices = newChargePoint.prices.filter((_, i) => i !== index);
                        setNewChargePoint({ ...newChargePoint, prices: newPrices });
                      }}
                      className="text-red-600 hover:text-red-800"
                    >
                      {t('chargePoints.remove')}
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  setNewChargePoint({
                    ...newChargePoint,
                    prices: [...newChargePoint.prices, { startTime: '', endTime: '', price: 0 }]
                  });
                }}
                className="text-blue-600 hover:text-blue-800"
              >
                {t('chargePoints.addTimePrice')}
              </button>
            </div>

            <div className="mt-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('chargePoints.connectors')}
              </label>
              {newChargePoint.connectors.map((connector, index) => (
                <div key={index} className="flex items-end gap-4 mb-2">
                  <div>
                    <label className="block text-xs">{t('chargePoints.connectorId')}</label>
                    <input
                      type="number"
                      value={connector.connectorId}
                      onChange={(e) => {
                        const newConnectors = [...newChargePoint.connectors];
                        newConnectors[index].connectorId = parseInt(e.target.value);
                        setNewChargePoint({ ...newChargePoint, connectors: newConnectors });
                      }}
                      className="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      required
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-xs">{t('chargePoints.status')}</label>
                    <select
                      value={connector.status}
                      onChange={(e) => {
                        const newConnectors = [...newChargePoint.connectors];
                        newConnectors[index].status = e.target.value;
                        setNewChargePoint({ ...newChargePoint, connectors: newConnectors });
                      }}
                      className="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    >
                      <option value="Available">{t('chargePoints.connectorStatus.Available')}</option>
                      <option value="Unavailable">{t('chargePoints.connectorStatus.Unavailable')}</option>
                      <option value="Reserved">{t('chargePoints.connectorStatus.Reserved')}</option>
                    </select>
                  </div>
                  {newChargePoint.connectors.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newConnectors = newChargePoint.connectors.filter((_, i) => i !== index);
                        setNewChargePoint({ ...newChargePoint, connectors: newConnectors });
                      }}
                      className="text-red-600 hover:text-red-800 mb-2"
                    >
                      {t('chargePoints.remove')}
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const lastConnector = newChargePoint.connectors[newChargePoint.connectors.length - 1];
                  const nextId = lastConnector ? lastConnector.connectorId + 1 : 1;
                  setNewChargePoint({
                    ...newChargePoint,
                    connectors: [...newChargePoint.connectors, { connectorId: nextId, status: 'Available' }]
                  });
                }}
                className="text-blue-600 hover:text-blue-800"
              >
                {t('chargePoints.addConnector')}
              </button>
            </div>
          </div>

          <div className="mt-4">
            <button
              type="submit"
              className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded mr-2"
            >
              {editingChargePoint ? t('chargePoints.editChargePoint') : t('chargePoints.addChargePoint')}
            </button>
          </div>
        </form>
      )}

      {/* Add filters and count summary */}
      {renderFilters()}
      {renderCountSummary()}

      <div 
        ref={tableContainerRef} 
        className="overflow-x-auto h-[600px] overflow-y-auto"
        onScroll={handleTableScroll}
      >
        <table className="min-w-full bg-white">
          <thead className="sticky top-0 bg-white shadow z-10">
            <tr>
              <th className="border px-4 py-2">{t('chargePoints.chargePointId')}</th>
              <th className="border px-4 py-2">{t('chargePoints.name')}</th>
              <th className="border px-4 py-2">{t('chargePoints.vendor')}</th>
              <th className="border px-4 py-2">{t('chargePoints.model')}</th>
              <th className="border px-4 py-2">{t('chargePoints.mode.title')}</th>
              <th className="border px-4 py-2">{t('chargePoints.vehicleType.title')}</th>
              <th className="border px-4 py-2">{t('chargePoints.location')}</th>
              <th className="border px-4 py-2">{t('chargePoints.type')}</th>
              <th className="border px-4 py-2">{t('chargePoints.power')}</th>
              <th className="border px-4 py-2">{t('chargePoints.connector')}</th>
              <th className="border px-4 py-2">{t('chargePoints.price')}</th>
              <th className="border px-4 py-2">{t('chargePoints.status')}</th>
              <th className="border px-4 py-2">{t('chargePoints.connectors')}</th>
              <th className="border px-4 py-2">{t('chargePoints.tableActions')}</th>
            </tr>
          </thead>
          <tbody>
            {chargePoints.map((chargePoint) => (
              <tr key={chargePoint._id}>
                <td className="border px-4 py-2">{chargePoint.chargePointId}</td>
                <td className="border px-4 py-2">{chargePoint.name}</td>
                <td className="border px-4 py-2">{chargePoint.vendor || ''}</td>
                <td className="border px-4 py-2">{chargePoint.model || ''}</td>
                <td className="border px-4 py-2">{t(`chargePoints.mode.${chargePoint.mode}`)}</td>
                <td className="border px-4 py-2">{t(`chargePoints.vehicleType.${chargePoint.vehicleType}`)}</td>
                <td className="border px-4 py-2">
                  {typeof chargePoint.locationId === 'object' && chargePoint.locationId !== null
                    ? chargePoint.locationId.name 
                    : t('chargePoints.messages.noLocation')}
                </td>
                <td className="border px-4 py-2">{t(`chargePoints.chargeTypes.${chargePoint.type}`)}</td>
                <td className="border px-4 py-2">{chargePoint.power} kW</td>
                <td className="border px-4 py-2">{t(`chargePoints.connectorTypes.${chargePoint.connector}`)}</td>
                <td className="border px-4 py-2">
                  {chargePoint.prices && chargePoint.prices.length > 0 ? (
                    <>
                      {formatPrice(getCurrentPrice(chargePoint.prices), chargePoint.operatorId)}
                      {t('chargePoints.messages.pricePerKwh')}
                      <div className="text-xs text-gray-500">
                        {chargePoint.prices.map((schedule, index) => (
                          <div key={index}>
                            {schedule.startTime}-{schedule.endTime}: {formatPrice(schedule.price, chargePoint.operatorId)}
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    t('chargePoints.messages.noPrice')
                  )}
                </td>
                <td className="border px-4 py-2">
                  <span className={`px-2 py-1 rounded ${getStatusColor(chargePoint.status)}`}>
                    {t(`chargePoints.chargePointStatus.${chargePoint.status}`)}
                  </span>
                </td>
                <td className="border px-4 py-2">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {chargePoint.connectors.map((connector) => (
                      <div 
                        key={connector.connectorId}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                      >
                        <span>#{connector.connectorId}</span>
                        <span className={`px-2 py-1 rounded ${getStatusColor(connector.status)}`}>
                          {t(`chargePoints.connectorStatus.${connector.status}`)}
                        </span>
                        <button 
                          style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px' }}
                          onClick={() => handleQRCode(chargePoint, connector)}
                          title={t('chargePoints.qrCode.title', { number: connector.connectorId })}
                        >
                          {Icons.qrcode}
                        </button>
                        <button 
                          style={{ 
                            border: 'none', 
                            background: 'none', 
                            cursor: 'pointer', 
                            padding: '4px',
                            color: isConnectorLocked(connector.status) ? 'red' : 'green'
                          }}
                          onClick={() => handleUnlock(chargePoint, connector.connectorId)}
                          title={isConnectorLocked(connector.status) 
                            ? t('chargePoints.controls.unlock')
                            : t('chargePoints.controls.lock')}
                          >
                          {isConnectorLocked(connector.status) ? Icons.lock : Icons.unlock}
                        </button>
                      </div>
                    ))}
                  </div>
                </td>
                <td className="border px-4 py-2">
                  <button
                    onClick={() => handleEdit(chargePoint)}
                    className="text-blue-600 hover:text-blue-800 mr-2"
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    onClick={() => handleRestart(chargePoint)}
                    className="text-yellow-600 hover:text-yellow-800 mr-2"
                  >
                    {t('chargePoints.controls.restart')}
                  </button>
                  <button
                    onClick={() => handleDelete(chargePoint._id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    {t('common.delete')}
                  </button>
                  <div className="relative inline-block ml-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveDropdown(activeDropdown === chargePoint._id ? null : chargePoint._id);
                      }}
                      className="text-gray-600 hover:text-gray-800 p-1 rounded-full hover:bg-gray-100"
                      title={t('chargePoints.moreOptions')}
                    >
                      {Icons.threeDots}
                    </button>
                    {activeDropdown === chargePoint._id && (
                      <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 dropdown-content">
                        <div className="py-1">
                          <button
                            onClick={() => handleEditPassword(chargePoint._id)}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            {t('chargePoints.setPassword')}
                          </button>
                          <button
                            onClick={() => handleShowUrlModal(chargePoint)}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            {t('chargePoints.copyUrl')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add loading indicator and load more trigger at the bottom */}
      <div className="text-center py-4">
        {isLoadingMore ? (
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mr-2"></div>
            {t('common.loading')}
          </div>
        ) : pagination.page < pagination.totalPages ? (
          <div>
            <button
              onClick={loadMoreItems}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            >
              {t('common.loadMore')}
            </button>
            <div className="mt-2 text-gray-600">
              {t(
                'chargePoints.showingCount', 
                `Showing ${chargePoints.length} of ${pagination.total} charge points`,
                { showing: chargePoints.length, total: pagination.total }
              )}
            </div>
          </div>
        ) : chargePoints.length > 0 ? (
          <div className="text-gray-600">
            {t('common.allItemsLoaded')}
          </div>
        ) : null}
      </div>

      {qrModalVisible && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.content}>
            <h3>{t('chargePoints.qrCode.title', { number: selectedConnector?.connectorId })}</h3>
            <button 
              style={modalStyles.closeButton}
              onClick={() => setQrModalVisible(false)}
            >
              {Icons.close}
            </button>
            {selectedConnector && (
              <div id="qr-code-content" className="flex flex-col items-center">
                <div id="qr-code" className="p-4 bg-white rounded">
                  <QRCodeSVG 
                    value={selectedConnector.qrCode}
                    size={256}
                    level="H"
                    includeMargin={true}
                    bgColor={"#FFFFFF"}
                    fgColor={"#000000"}
                  />
                </div>
                <p className="mt-4 text-center">
                  {t('chargePoints.chargePointId')}: {selectedConnector.chargePointId}<br />
                  {t('chargePoints.connectorId')}: #{selectedConnector.connectorId}
                </p>
                <div className="mt-4 flex gap-4">
                  <button
                    onClick={downloadQRCode}
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                  >
                    {t('chargePoints.qrCode.download')}
                  </button>
                  <button
                    onClick={printQRCode}
                    className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                  >
                    {t('chargePoints.qrCode.print')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showPasswordModal && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.content} className="w-96">
            <h3 className="text-lg font-bold mb-4">{t('chargePoints.password.title')}</h3>
            <button 
              style={modalStyles.closeButton}
              onClick={() => {
                setShowPasswordModal(false);
                setNewPassword('');
                setShowPassword(false);
              }}
            >
              {Icons.close}
            </button>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {currentPassword ? t('chargePoints.password.current') : t('chargePoints.password.new')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline pr-10"
                  placeholder={currentPassword ? '********' : t('chargePoints.messages.enterNewPassword')}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? Icons.eyeOff : Icons.eye}
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {t('chargePoints.messages.passwordOptional')}
              </p>
            </div>
            <div className="flex justify-end gap-2">
              {currentPassword && (
                <button
                  onClick={() => handlePasswordUpdate('')}
                  className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                >
                  {t('chargePoints.password.remove')}
                </button>
              )}
              <button
                onClick={() => handlePasswordUpdate()}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showUrlModal && selectedUrlChargePoint && (
        <Modal
          isOpen={showUrlModal}
          onClose={() => {
            setShowUrlModal(false);
            setSelectedUrlChargePoint(null);
          }}
          title={t('chargePoints.urlDetails')}
        >
          <div className="space-y-4 p-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('chargePoints.ipDomain')}
              </label>
              <div className="flex">
                <input
                  type="text"
                  className="flex-1 rounded-l-md border-r-0 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value="wss://ws.goldtek.vn"
                  readOnly
                />
                <button
                  onClick={() => handleCopyToClipboard('wss://ws.goldtek.vn')}
                  className="rounded-r-md border border-l-0 border-gray-300 px-3 py-2 hover:bg-gray-50"
                >
                  {Icons.copy}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('chargePoints.path')}
              </label>
              <div className="flex">
                <input
                  type="text"
                  className="flex-1 rounded-l-md border-r-0 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={selectedUrlChargePoint.chargePointId}
                  readOnly
                />
                <button
                  onClick={() => handleCopyToClipboard(selectedUrlChargePoint.chargePointId)}
                  className="rounded-r-md border border-l-0 border-gray-300 px-3 py-2 hover:bg-gray-50"
                >
                  {Icons.copy}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('chargePoints.authKey')}
              </label>
              <div className="flex">
                <input
                  type="text"
                  className="flex-1 rounded-l-md border-r-0 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={selectedUrlChargePoint.password || t('chargePoints.noPassword')}
                  readOnly
                />
                <button
                  onClick={() => selectedUrlChargePoint.password && handleCopyToClipboard(selectedUrlChargePoint.password)}
                  className={`rounded-r-md border border-l-0 border-gray-300 px-3 py-2 ${
                    selectedUrlChargePoint.password ? 'hover:bg-gray-50 cursor-pointer' : 'opacity-50 cursor-not-allowed'
                  }`}
                  disabled={!selectedUrlChargePoint.password}
                  title={selectedUrlChargePoint.password ? t('chargePoints.copyAuthKey') : t('chargePoints.noAuthKey')}
                >
                  {Icons.copy}
                </button>
              </div>
              {!selectedUrlChargePoint.password && (
                <p className="text-sm text-gray-500 mt-1">
                  {t('chargePoints.noPasswordHint')}
                </p>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Add location search modal */}
      {showLocationModal && (
        <div style={modalStyles.overlay}>
          <div style={{ ...modalStyles.content, width: '90%', maxWidth: '800px', maxHeight: '80vh' }}>
            <h3 className="text-xl font-bold mb-4">{t('chargePoints.searchLocation')}</h3>
            <button 
              style={modalStyles.closeButton}
              onClick={() => setShowLocationModal(false)}
            >
              {Icons.close}
            </button>
            
            <div className="mb-4 relative">
              <input
                type="text"
                placeholder={t('chargePoints.searchLocationPlaceholder')}
                className="w-full px-3 py-2 pr-10 border rounded"
                value={locationSearchTerm}
                onChange={(e) => {
                  const term = e.target.value;
                  setLocationSearchTerm(term);
                  debouncedLocationSearch(term);
                }}
                autoFocus
              />
              {locationSearchTerm && (
                <button
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  onClick={() => {
                    setLocationSearchTerm('');
                    setSearchedLocations([]);
                  }}
                  type="button"
                  aria-label="Clear search"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            
            <div className="mb-2 text-sm text-gray-600">
              {isSearchingLocations ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                  {t('common.searching')}
                </div>
              ) : searchedLocations.length > 0 ? (
                t('chargePoints.locationsFound', { count: searchedLocations.length })
              ) : locationSearchTerm.length > 1 ? (
                t('chargePoints.noLocationsFound')
              ) : (
                t('chargePoints.typeToSearchLocations')
              )}
            </div>
            
            <div className="max-h-96 overflow-y-auto border border-gray-300 rounded">
              {searchedLocations.length > 0 ? (
                searchedLocations.map(location => (
                  <div 
                    key={location._id}
                    className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-200 transition-colors duration-100"
                    onClick={() => selectLocation(location)}
                  >
                    <div className="font-medium">{location.name}</div>
                    {location.address && typeof location.address === 'object' && (
                      <div className="text-sm text-gray-600">
                        {location.address.street && `${location.address.street}, `}
                        {location.address.city && `${location.address.city}, `}
                        {location.address.country}
                      </div>
                    )}
                  </div>
                ))
              ) : locationSearchTerm.length > 1 && !isSearchingLocations ? (
                <div className="p-4 text-center text-gray-500">
                  {t('chargePoints.noLocationsFoundTryAgain')}
                </div>
              ) : locationSearchTerm.length < 2 ? (
                <div className="p-4 text-center text-gray-500">
                  {t('chargePoints.enterSearchTerms')}
                </div>
              ) : null}
            </div>
            
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setShowLocationModal(false)}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded mr-2"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChargePointList; 